import { getRedDataApiPaths } from '../config'
import { BrandDetails, Reward } from '../redux/rewards/types'
import { RewardEarn } from '../redux/rewardsEarn/types'
import { ViewingRegion } from '../utils'
import { get } from '../utils/httpClient'
import { getApiUrlParameters } from './discovery.api'
import { keyBy, values } from 'lodash'

export type BrandsData = {
  brands: BrandDetails[]
}

export type BrandDetailsWithRewards = BrandDetails & {
  earnRewards: RewardEarn[]
  spendRewards: Reward[]
}

export const apiGetAllBrands = async (viewingRegion: ViewingRegion): Promise<BrandsData> =>
  await get(`${getRedDataApiPaths().RED_API_DISCOVERY_BRANDS}?viewingRegion=${viewingRegion}`, true)

export const apiGetBrandBySlug = async (isAuthenticated: boolean, slug: string, viewingRegion: ViewingRegion): Promise<BrandDetails> => {
  const url = isAuthenticated
    ? `${getRedDataApiPaths().RED_API_DISCOVERY_BRANDS}/${slug}?viewingRegion=${viewingRegion}`
    : `${getRedDataApiPaths().RED_API_DISCOVERY_BRANDS}/public/${slug}?viewingRegion=${viewingRegion}`

  return await get(url, isAuthenticated)
}

export const apiGetBrandBySlugWithRewards = async (
  isAuthenticated: boolean,
  slug: string,
  viewingRegion: ViewingRegion
): Promise<BrandDetailsWithRewards> => {
  // Get the brand details
  const brand = await apiGetBrandBySlug(isAuthenticated, slug, viewingRegion)
  // Get the rewards for this brand
  const { earnRewards, spendRewards } = await apiGetRewardsByBrand(isAuthenticated, brand.brandName)

  return {
    ...brand,
    earnRewards,
    spendRewards,
  }
}

export const apiGetRewardsByBrand = async (
  isAuthenticated: boolean,
  brandName: string
): Promise<{ earnRewards: RewardEarn[]; spendRewards: Reward[] }> => {
  // use getApiUrlParameters to build the query params for each api call
  const earnQueryString = getApiUrlParameters()
  const spendQueryString = getApiUrlParameters({ enableVariantGrouping: true })
  const spendQueryStringWithBrands = getApiUrlParameters({ enableVariantGrouping: true, isBrands: true })

  const earnUrl = isAuthenticated
    ? `${getRedDataApiPaths().RED_API_DISCOVERY_REWARDS_EARN}?${earnQueryString}`
    : `${getRedDataApiPaths().RED_API_DISCOVERY_REWARDS_EARN_PUBLIC}?${earnQueryString}`

  const spendUrl = isAuthenticated
    ? `${getRedDataApiPaths().RED_API_DISCOVERY_REWARDS_SPEND}?${spendQueryString}`
    : `${getRedDataApiPaths().RED_API_DISCOVERY_REWARDS_SPEND_PUBLIC}?${spendQueryString}`

  const spendUrlWithBrands = isAuthenticated
    ? `${getRedDataApiPaths().RED_API_DISCOVERY_REWARDS_SPEND}?${spendQueryStringWithBrands}`
    : `${getRedDataApiPaths().RED_API_DISCOVERY_REWARDS_SPEND_PUBLIC}?${spendQueryStringWithBrands}`

  // Request in parallel via react-query
  const [earnResponse, spendResponse, spendWithBrandsResponse] = await Promise.all([
    get(earnUrl, isAuthenticated),
    get(spendUrl, isAuthenticated),
    get(spendUrlWithBrands, isAuthenticated),
  ])

  // Filter earn rewards by brand name
  const earnRewards = earnResponse.rewards.filter((reward: RewardEarn) => reward.content.brandDetails.brandName === brandName)

  // Filter both spend responses by brand name
  const spendRewardsNormal = spendResponse.rewards.filter((reward: Reward) => reward.content.brandDetails.brandName === brandName)

  const spendRewardsWithBrands = spendWithBrandsResponse.rewards.filter(
    (reward: Reward) => reward.content.brandDetails.brandName === brandName
  )

  const combinedRewards = [...spendRewardsNormal, ...spendRewardsWithBrands]
  const spendRewards = values(keyBy(combinedRewards, 'rewardId'))

  return { earnRewards, spendRewards }
}
